import React from "react";

import {EditOutlined, HistoryOutlined, LinkOutlined} from "@ant-design/icons";
import {Col, Descriptions, Row, Statistic, Tabs} from "antd";

import {projectCurrency, ROP_MIN_LABEL} from "../../properties";
import {MaterialInfo} from "../../variants";
import {DataCard} from "../helpers/cards/Card";
import {MaterialBlock} from "../helpers/cards/MaterialBlock";
import {StandardTable} from "../helpers/TablePanel";

const {TabPane} = Tabs;

export function MaterialCard({material, itemInfo, canEdit, onEdit, onHistoryOpen, leadTime}) {
	const showValues = data => {
		if (Array.isArray(data)) {
			return data.join(", ");
		}

		return data;
	};

	const getLeadTimeColumns = () => [
		{
			dataIndex: "Purchasing Document",
			title: "PO #",
		},
		{
			dataIndex: "Scheduled Quantity",
			title: "Quantity",
		},
		{
			dataIndex: "Purchase Order Date",
			title: "Purchase Order Date",
		},
		{
			dataIndex: "Delivery Date",
			title: "Delivery Date",
			render: date => date.split(" ")[0],
		},
		{
			dataIndex: "Lead Time in Days",
			title: "Planned Delivery Time",
		},
		{
			dataIndex: "Actual Lead Time",
			title: "Actual Delivery Time",
		},
		{
			dataIndex: "Actual Lead Time",
			title: "Diff in days",
			render: (lt_actual, item) => Math.abs(lt_actual - item["Lead Time in Days"]),
		},
		{
			dataIndex: "Actual Lead Time",
			title: "Diff in %",
			render: (lt_actual, item) =>
				Math.round((Math.abs(lt_actual - item["Lead Time in Days"]) / item["Lead Time in Days"]) * 100),
		},
	];

	return (
		<DataCard
			title={
				<Row>
					<Col span={20}>
						<Row>
							<div style={{marginRight: 5}}>Material:</div>
							<MaterialBlock
								material={material}
								children={
									<div>
										{material} - {itemInfo.description} <LinkOutlined />
									</div>
								}
							/>
							{canEdit && !itemInfo.approved && (
								<EditOutlined
									style={{marginLeft: 5, marginTop: 5}}
									onClick={() => onEdit({editModal: true})}
								/>
							)}
						</Row>
					</Col>
					<Col span={4}>
						<span
							className="url-link float-right"
							style={{marginLeft: 10}}
							onClick={() => onHistoryOpen({historyModal: true})}>
							History <HistoryOutlined />
						</span>
					</Col>
				</Row>
			}>
			{itemInfo.approved && (
				<div style={{color: "green"}}>The {ROP_MIN_LABEL}/MAXES were overridden manually!</div>
			)}
			{itemInfo["valid_for_optimisation"] === 0 && (
				<div style={{color: "red"}}>Material {material} is not included in any of existing scenarios!</div>
			)}
			<Tabs defaultActiveKey={"1"} style={{marginTop: -15}}>
				<TabPane tab={"Material Data"} key={"1"}>
                    <div>{MaterialInfo.MainAttributes(itemInfo, showValues)}</div>
				</TabPane>
                {itemInfo.plant_info && (
                    <TabPane tab={`SKU's Data (${itemInfo.plant_info.length || 0})`} key={"2"}>
                        <StandardTable
                            tableSize={"small"}
                            data={itemInfo.plant_info}
                            columns={MaterialInfo.SKUTableColumns()}
                        />
                    </TabPane>
                )}
				<TabPane tab={"Where-Used factors"} key={"3"}>
					<Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
						<Descriptions.Item label="Installed per Unit (Max)">{itemInfo.max_installed}</Descriptions.Item>
						<Descriptions.Item span={2} label="Highest Criticality">
							{itemInfo.criticality}
						</Descriptions.Item>
						<Descriptions.Item label="Total Quantity Installed">
							{itemInfo.total_installed}
						</Descriptions.Item>
					</Descriptions>
				</TabPane>
				<TabPane tab={`Lead Time Variation (${leadTime.length})`} key={"4"}>
					<StandardTable tableSize={"small"} data={leadTime} columns={getLeadTimeColumns()} />
				</TabPane>
                {itemInfo.demand_info && (
                    <TabPane tab={"Demand Analysis"} key={"demand_analysis"}>
                        <Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
                            <Descriptions.Item span={2} label="Demand Quantity Variability">
                                {itemInfo.demand_info.qty_cov}
                            </Descriptions.Item>
                            <Descriptions.Item label="Demand Time Variability">
                                {itemInfo.demand_info.time_cov}
                            </Descriptions.Item>
                            <Descriptions.Item span={3} label="Forecastability Type">
                                {itemInfo.demand_info.forecastability_type}
                            </Descriptions.Item>
                        </Descriptions>
                    </TabPane>
                )}
                {itemInfo.po_info && (
                    <TabPane tab={"Supply Analysis"} key={"po_analysis"}>
                        <Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
                            <Descriptions.Item span={2} label="Total PO">
                                {itemInfo.po_info.total_pos}
                            </Descriptions.Item>
                            <Descriptions.Item label="Planned Lead Time (days)">
                                {itemInfo.po_info.planned_lead_time}
                            </Descriptions.Item>
                            <Descriptions.Item span={3} label="PO to RCPT">
                                <Row>
                                    <Col span={8}>
                                        <Statistic
                                            title={"Mean"}
                                            value={itemInfo.po_info.po_to_rcpt_analysis.mean || "NA"}
                                            valueStyle={{fontSize: 14}}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title={"Std Deviation"}
                                            value={itemInfo.po_info.po_to_rcpt_analysis.std_dev}
                                            valueStyle={{fontSize: 14}}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title={"CoV"}
                                            value={itemInfo.po_info.po_to_rcpt_analysis.cov}
                                            valueStyle={{fontSize: 14}}
                                        />
                                    </Col>
                                </Row>
                            </Descriptions.Item>
                            <Descriptions.Item span={3} label="PR to PO">
                                <Row>
                                    <Col span={8}>
                                        <Statistic
                                            title={"Mean"}
                                            value={itemInfo.po_info.pr_to_po_analysis.mean}
                                            valueStyle={{fontSize: 14}}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title={"Std Deviation"}
                                            value={itemInfo.po_info.pr_to_po_analysis.std_dev}
                                            valueStyle={{fontSize: 14}}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title={"CoV"}
                                            value={itemInfo.po_info.pr_to_po_analysis.cov}
                                            valueStyle={{fontSize: 14}}
                                        />
                                    </Col>
                                </Row>
                            </Descriptions.Item>
                        </Descriptions>
                    </TabPane>
                )}
            </Tabs>
		</DataCard>
	);
}
